// ── Schulkarten (M27) ────────────────────────────────────────────────────────
// Backend: functions/intranet/src/functions/schulkarten.js (Modul 'schulen-karten').
// Die PIN kommt von POST /api/schulkarten/pin genau EINMAL zurück: sie läuft
// nur über eine Mutation und wird nie in einem Query-Cache oder Storage abgelegt.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../../shared/api'

export interface SchoolCard {
  id: string
  schoolId: string
  schoolName: string
  kuerzel: string | null
  chip: string
  bezeichnung: string
  status: 'aktiv' | 'inaktiv'
  gueltigVon: string            // YYYY-MM-DD
  gueltigBis: string | null     // YYYY-MM-DD
  letzteAnmeldung: string | null
  fehlversuche: number
  angelegtVon: string | null
  geaendertAm: string | null
}

export interface SchoolPinInfo {
  version: number
  gesetztVon: string | null
  gesetztAm: string | null
}

export interface SchoolCardsResponse {
  karten: SchoolCard[]
  pin: SchoolPinInfo | null
}

const KEY = ['schulkarten'] as const

/** Karten + PIN-Stand einer Schule (schoolId = dim.School-Id als Text). */
export function useSchoolCards(schoolId: string) {
  return useQuery({
    queryKey: [...KEY, 'school', schoolId],
    queryFn: () => api.get<SchoolCardsResponse>(`/api/schulkarten?schoolId=${encodeURIComponent(schoolId)}`),
    enabled: !!schoolId,
    staleTime: 30_000,
  })
}

/** Alle Karten aller Schulen (Übersichtsseite). */
export function useAllSchoolCards() {
  return useQuery({
    queryKey: [...KEY, 'all'],
    queryFn: () => api.get<SchoolCardsResponse>('/api/schulkarten'),
    staleTime: 30_000,
  })
}

export interface CardCreateInput {
  schoolId: string
  chip: string
  bezeichnung: string
  gueltigVon: string
  gueltigBis: string | null
}

export function useCreateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CardCreateInput) => api.post<{ id: string }>('/api/schulkarten', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export interface CardPatchInput {
  id: string
  status?: 'aktiv' | 'inaktiv'
  bezeichnung?: string
  gueltigVon?: string
  gueltigBis?: string | null
}

export function usePatchCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: CardPatchInput) =>
      api.patch<{ id: string; sitzungenBeendet: number }>(`/api/schulkarten/${encodeURIComponent(id)}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

/** Neue PIN erzeugen. Das Ergebnis NUR im Komponenten-State halten, bis der Dialog schließt. */
export function useCreatePin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (schoolId: string) => api.post<{ pin: string; version: number }>('/api/schulkarten/pin', { schoolId }),
    // Ergebnis nicht im Mutation-Cache halten (Standard wären 5 Minuten im Speicher).
    gcTime: 0,
    // Kein Ergebnis in den Cache: nur die Liste (PIN-Stand) neu laden.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

/** Deutscher Fehlertext des Servers ({error}) bzw. der Fehlermeldung. */
export function kartenFehler(e: unknown): string {
  if (e instanceof ApiError || e instanceof Error) return e.message
  return String(e)
}

// ── Anzeige-Helfer ───────────────────────────────────────────────────────────

/** 'YYYY-MM-DD…' -> 'dd.MM.yyyy'; leer -> '—'. */
export function datumDe(iso: string | null | undefined): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

export function heuteIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function plusJahrIso(iso: string, jahre = 1): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y + jahre, m - 1, d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

export type KartenZustand = 'aktiv' | 'gesperrt' | 'abgelaufen' | 'bald' | 'noch-nicht'

/** Zustand für Anzeige/Filter. 'bald' = aktiv und läuft innerhalb von 30 Tagen ab. */
export function kartenZustand(k: Pick<SchoolCard, 'status' | 'gueltigVon' | 'gueltigBis'>, heute = heuteIso()): KartenZustand {
  if (k.status === 'inaktiv') return 'gesperrt'
  if (k.gueltigBis && k.gueltigBis < heute) return 'abgelaufen'
  if (k.gueltigVon > heute) return 'noch-nicht'
  if (k.gueltigBis && k.gueltigBis <= plusTageIso(heute, 30)) return 'bald'
  return 'aktiv'
}

function plusTageIso(iso: string, tage: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d + tage)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}
